import { isRetryableNetworkError, reportApiFailure } from "./api-errors";

type RetryOnNetworkErrorOptions = {
  /** When false, skip reportApiFailure on final failure (caller logs instead). Default true. */
  reportFailure?: boolean;
  /** Called once on the first retryable failure, before the first backoff/retry. */
  onFirstRetryableFailure?: (error: unknown) => void;
};

/**
 * Retry function with exponential backoff for network errors
 */
export async function retryOnNetworkError<T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries: number = 3,
  { reportFailure = true, onFirstRetryableFailure }: RetryOnNetworkErrorOptions = {},
): Promise<T> {
  const initialMaxRetries = maxRetries;

  async function retryWithBackoff<TInner>(
    op: () => Promise<TInner>,
    name: string,
    remainingRetries: number,
  ): Promise<TInner> {
    try {
      return await op();
    } catch (error) {
      if (isRetryableNetworkError(error) && remainingRetries > 1) {
        if (remainingRetries === initialMaxRetries) {
          onFirstRetryableFailure?.(error);
        }

        console.warn(
          `${name} failed with network error, retrying... (${remainingRetries - 1} attempts left)`,
        );

        // Wait a bit before retrying (exponential backoff)
        const attemptNumber = initialMaxRetries - remainingRetries + 1;
        await new Promise((resolve) => {
          setTimeout(resolve, 2 ** (attemptNumber - 1) * 1000);
        });

        return retryWithBackoff(op, name, remainingRetries - 1);
      }

      if (reportFailure) {
        reportApiFailure(name, error);
      }

      throw error;
    }
  }

  return retryWithBackoff(operation, operationName, maxRetries);
}
